import { inject, Injectable, signal } from '@angular/core';
import { BasketItem } from '../models';
import { catchError, EMPTY, finalize, Observable, tap } from 'rxjs';
import { WINDOW } from '@retail-portal-santander-frontend/shared-utils';
import {
  BffQuotesService,
  CreateContractRequestDTO,
  CreateContractResponseDTO,
  QuoteResponseDTO,
  RetryPaymentResponseDTO,
} from '@retail-portal-santander-frontend/generated-api';
import { PAYMENT_URL } from '../tokens';
import { skipToastContext } from '@retail-portal-santander-frontend/remote-shared-utils';

@Injectable({
  providedIn: 'root',
})
export class QuotationService {
  private readonly window = inject(WINDOW);
  private readonly bffQuotesService = inject(BffQuotesService);
  private readonly basePaymentUrl = inject(PAYMENT_URL);

  readonly basket = signal<BasketItem>(null);
  readonly loading = signal<boolean>(false);
  readonly policyId = signal<string>(null);
  readonly paymentUrl = signal<string>(null);

  private get urlParams(): URLSearchParams {
    return new URLSearchParams(this.window.location.search);
  }

  private get policyNumber(): string {
    return this.urlParams.get('policyNumber');
  }

  navigateToMetaportal(): void {
    this.window?.shell?.policyDetailsMotorSantander?.({ policyNumber: this.policyNumber });
  }

  getQuote(): Observable<QuoteResponseDTO> {
    this.loading.set(true);

    return this.bffQuotesService.getQuotation().pipe(
      catchError(() => EMPTY),
      finalize(() => this.loading.set(false))
    );
  }

  createContract(data: CreateContractRequestDTO): Observable<CreateContractResponseDTO> {
    this.loading.set(true);

    return this.bffQuotesService
      .createContract({
        createContractRequestDTO: {
          ...data,
          parentPolicyId: this.policyNumber || null,
        },
      })
      .pipe(
        tap((data: CreateContractResponseDTO) => {
          this.policyId.set(data.policyId);
          this.paymentUrl.set(`${this.basePaymentUrl}${data.paymentId}`);
        }),
        finalize(() => this.loading.set(false))
      );
  }

  refreshPaymentSession(): Observable<RetryPaymentResponseDTO> {
    this.loading.set(true);

    return this.bffQuotesService
      .retryPayment({ policyNumber: this.policyId() }, 'body', false, { context: skipToastContext(true) })
      .pipe(
        tap((data: RetryPaymentResponseDTO) => {
          this.paymentUrl.set(`${this.basePaymentUrl}${data.paymentId}`);
        }),
        finalize(() => this.loading.set(false))
      );
  }
}
